<script lang="ts">
	// @ts-nocheck
	import slugify from 'slugify';
	import { cn } from '$lib/utils.js';
	import { File, ArrowRight, ChevronDown, Percent } from 'lucide-svelte/icons';
	import * as Popover from '$lib/components/ui/popover';
	import * as Alert from '$lib/components/ui/alert';
	import * as Tabs from '$lib/components/ui/tabs';
	import allToursData from '$lib/data/allTours.json';
	import { enhancedImages } from '$lib/utils/images';
	import Button from '../ui/button/button.svelte';
	import Links from './Links.svelte';
	import TasIcon from '$lib/assets/icons/TasIcon.svelte';

	// Product code arrays for each tour type - these define the desired order
	const dayTourCodes = ['P18ZC5', 'PJHLE0', 'PNVP8M', 'P01GQC', 'PQ5CJC', 'PTC0KJ'];
	const bundledDayTourCodes = [
		'PU9VRB',
		'P6GE12',
		'P1AHF0',
		'P0QKC2',
		'PHKZNS',
		'PKQPM9',
		'PTNCNK',
		'PM5WCG',
		'PBRPKL'
	];
	const multiDayTourCodes = ['PTWH1T', 'PVHGUQ', 'P1VDC1', 'P0V9R2', 'P8JLJC'];
	const shoreExcursionCodes = ['P0J8QR', 'PM0VRV', 'PB2LG2', 'PSP9WG', 'P9TVKQ', 'PKLVUQ'];

	// Track if day tours megamenu has been opened (resets on page refresh)
	let showDayToursBadge = $state(true);
	let dayToursOpen = $state(false);
	let dayToursTab = $state('dayTours');

	function handleDayToursOpen(open: boolean) {
		dayToursOpen = open;
		if (open && showDayToursBadge) {
			showDayToursBadge = false;
		}
	}

	// Create a map for quick lookup of tours by product code
	let toursByCode = $derived(
		allToursData.reduce((map, tour) => {
			map[tour.productCode] = tour;
			return map;
		}, {})
	);

	// Build the tour arrays in the specified order
	let dayTours = $derived(
		dayTourCodes
			.map((code) => toursByCode[code]) // Get the tour for each code
			.filter((tour) => tour !== undefined) // Filter out any codes not found in allToursData
	);

	let bundledDayTours = $derived(
		bundledDayTourCodes.map((code) => toursByCode[code]).filter((tour) => tour !== undefined)
	);

	let multiDayTours = $derived(
		multiDayTourCodes.map((code) => toursByCode[code]).filter((tour) => tour !== undefined)
	);

	let shoreExcursions = $derived(
		shoreExcursionCodes.map((code) => toursByCode[code]).filter((tour) => tour !== undefined)
	);

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;
		return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
	}

	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;

		// If it's an enhanced image, get it from the enhancedImages collection
		const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		if (enhancedEntry) {
			return enhancedEntry[1].default;
		}

		// Otherwise, use the URL directly
		return imageUrl;
	}
</script>

{#snippet tourTile(tour, titleClass)}
	<Popover.Close>
		<a
			data-sveltekit-noscroll
			href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
			data-sveltekit-prefetch
			class="group relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
		>
			{#if tour.images && tour.images.length > 0}
				{#if isEnhancedImage(tour.images[0].itemUrl)}
					<enhanced:img
						alt={tour.name}
						class="absolute inset-0 h-full w-full object-cover"
						src={getImageSource(tour.images[0].itemUrl)}
						sizes="(min-width: 1280px) 16vw, 50vw"
						loading="lazy"
					/>
				{:else}
					<img
						alt={tour.name}
						class="absolute inset-0 h-full w-full object-cover"
						src={tour.images[0].itemUrl}
						sizes="(min-width: 1280px) 16vw, 50vw"
						loading="lazy"
					/>
				{/if}
			{:else}
				<File class="h-48 w-full object-cover text-muted-foreground" />
			{/if}
			<div
				class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
			></div>

			<div class="relative mx-auto lg:mx-0">
				<h3 class={cn('text-center font-bold text-background', titleClass)}>{tour.name}</h3>
			</div>
			<div
				class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
			>
				<ArrowRight class="h-5 w-5 text-background" />
			</div>
		</a>
	</Popover.Close>
{/snippet}

<nav class="flex items-center gap-6 text-sm">
	<Popover.Root bind:open={dayToursOpen} onOpenChange={handleDayToursOpen}>
		<Popover.Trigger
			class={cn('relative flex items-center transition-colors hover:text-foreground/80')}
		>
			Day tours <ChevronDown class=" mt-1 h-4 w-4" />
			{#if showDayToursBadge}
				<div
					class="absolute -left-1 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-red-500/90 text-xs text-white"
				></div>
				<div
					class="absolute -left-1 -top-0.5 flex h-2 w-2 animate-ping items-center justify-center rounded-full bg-red-500/80 text-xs text-white"
				></div>
			{/if}
		</Popover.Trigger>

		<Popover.Content
			class="flex w-[1250px] flex-col overflow-hidden rounded-xl p-0"
			align="start"
			sideOffset={24}
		>
			<Tabs.Root bind:value={dayToursTab}>
				<div class="flex w-full items-start gap-6 bg-secondary p-6">
					<div class="flex flex-1 flex-col">
						<Tabs.List class="mb-3 h-full w-fit rounded-full bg-background px-2 py-1.5">
							<Tabs.Trigger
								class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
								value="dayTours">Day tours</Tabs.Trigger
							>
							<Tabs.Trigger
								class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
								value="bundledDayTours">Bundled day tours</Tabs.Trigger
							>
						</Tabs.List>
						{#if dayToursTab === 'bundledDayTours'}
							<p class="max-w-3xl text-sm">
								Make the most of your time in Tasmania by combining our most popular day tours into
								one great-value package. Our bundled day tours pair iconic destinations across
								multiple days, so you can see more of the island while saving on every tour you add.
							</p>
						{:else}
							<p class="max-w-3xl text-sm">
								Get the best deals on Hobart Tours here! See Australia's best kept secret and hear
								the many stories that have made this island so very desirable. If you're on the
								lookout for day tours from Hobart, Tassie Tours offers a range of exciting
								discounted day tours in Tasmania to choose from.
							</p>
						{/if}
					</div>
					<Popover.Close>
						<a href="/tours#dayTours" data-sveltekit-noscroll data-sveltekit-prefetch>
							<Alert.Root class="relative w-[300px] self-center rounded-2xl bg-tt-red text-white">
								<Alert.Description class="text-md px-2 text-center">
									<span class="">Book 2+ day tours</span> and receive
									<span class="font-black">15% OFF</span> your order
								</Alert.Description>
								<div
									class="absolute -left-3 -top-3 m-0 flex h-12 w-12 items-center justify-center p-0"
								>
									<div class="relative flex h-full w-full items-center justify-center">
										<TasIcon class="shadow-lg" />
										<div class="absolute inset-0 flex items-center justify-center">
											<Percent class="h-5 w-5 stroke-foreground" />
										</div>
									</div>
								</div>
							</Alert.Root></a
						></Popover.Close
					>
				</div>
				<Tabs.Content value="dayTours" class="mt-0">
					<div class="grid grid-cols-6 gap-4 px-4 pt-6">
						{#each dayTours.slice(0, 6) as tour}
							{@render tourTile(tour, 'text-md')}
						{/each}
					</div>
				</Tabs.Content>
				<Tabs.Content value="bundledDayTours" class="mt-0">
					<div class="grid grid-cols-6 gap-4 px-4 pt-6">
						{#each bundledDayTours.slice(0, 6) as tour}
							{@render tourTile(tour, 'line-clamp-4 text-sm')}
						{/each}
					</div>
				</Tabs.Content>
				<div class="flex rounded-xl px-6 pb-2">
					<Popover.Close>
						<Button
							href={`/tours#${dayToursTab}`}
							variant="link"
							class="pl-0 text-sm font-semibold text-foreground"
						>
							View more <ArrowRight class="ml-2 h-4 w-4" />
						</Button>
					</Popover.Close>
				</div>
			</Tabs.Root></Popover.Content
		>
	</Popover.Root>
	<Popover.Root>
		<Popover.Trigger class="flex items-center transition-colors hover:text-foreground/80">
			Multi-day tours <ChevronDown class=" mt-1 h-4 w-4" />
		</Popover.Trigger>

		<Popover.Content
			class="flex w-[1250px] flex-col overflow-hidden rounded-xl p-0"
			align="start"
			sideOffset={24}
		>
			<div class="w-full bg-secondary">
				<div class="flex flex-col rounded-xl p-6">
					<h2
						class="scroll-m-20 pb-1 font-heading text-xl font-semibold tracking-tight transition-colors first:mt-0"
					>
						Multi-day Tours
					</h2>
					<!-- <div class="mb-4 h-1 w-10 rounded-full bg-tt-red"></div> -->
					<p class="max-w-xl text-sm">
						It's not only easier but your travelling with a award winning tour company who prides
						itself on professionalism, fun and consistency. It will also give you a chance to meet
						more of the Tassie Tours team who will all share their unique Tasmanian story.
					</p>
				</div>
			</div>
			<div class="grid grid-cols-5 gap-4 px-4 pt-6">
				{#each multiDayTours.slice(0, 5) as tour}
					<Popover.Close>
						<a
							data-sveltekit-noscroll
							href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
							data-sveltekit-prefetch
							class="group relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
						>
							{#if tour.images && tour.images.length > 0}
								{#if isEnhancedImage(tour.images[0].itemUrl)}
									<enhanced:img
										alt={tour.name}
										class="absolute inset-0 h-full w-full object-cover"
										src={getImageSource(tour.images[0].itemUrl)}
										sizes="(min-width: 1280px) 16vw, 50vw"
										loading="lazy"
									/>
								{:else}
									<img
										alt={tour.name}
										class="absolute inset-0 h-full w-full object-cover"
										src={tour.images[0].itemUrl}
										sizes="(min-width: 1280px) 16vw, 50vw"
										loading="lazy"
									/>
								{/if}
							{:else}
								<File class="h-48 w-full object-cover text-muted-foreground" />
							{/if}
							<div
								class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
							></div>

							<div class="relative mx-auto lg:mx-0">
								<h3 class="text-center text-sm font-bold text-background">{tour.name}</h3>
							</div>
							<div
								class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
							>
								<ArrowRight class="h-5 w-5 text-background" />
							</div>
						</a>
					</Popover.Close>
				{/each}
			</div>
			<div class="flex rounded-xl px-6 pb-2">
				<Popover.Close>
					<Button
						href="/tours#multiDayTours"
						variant="link"
						class="pl-0 text-sm font-semibold text-foreground"
					>
						View more <ArrowRight class="ml-2 h-4 w-4" />
					</Button>
				</Popover.Close>
			</div></Popover.Content
		>
	</Popover.Root>
	<Popover.Root>
		<Popover.Trigger class="flex items-center transition-colors hover:text-foreground/80">
			Private tours <ChevronDown class=" mt-1 h-4 w-4" />
		</Popover.Trigger>

		<Popover.Content
			class="flex w-[1250px] flex-col overflow-hidden rounded-xl p-0"
			align="start"
			sideOffset={24}
		>
			<div class="w-full bg-secondary">
				<div class="flex flex-col rounded-xl p-6">
					<h2
						class="scroll-m-20 pb-1 font-heading text-xl font-semibold tracking-tight transition-colors first:mt-0"
					>
						Private Tours
					</h2>
					<div class="grid grid-cols-2 gap-4">
						<p class=" text-sm">
							Whether you're an adventurous solo traveller or part of a small to medium-sized group,
							our private tour packages are the ultimate way to discover the wonders of Tasmania.
							Let us take care of all the details while you immerse yourself in the beauty and charm
							of this incredible island.
						</p>
						<p class=" text-sm">
							Experience the ultimate Tasmanian adventure with Tassie Tours, where your dream
							holiday becomes a reality. Whether you're seeking a one-day escapade or a multi-day
							exploration, we specialise in tailoring unforgettable experiences to suit your unique
							desires.
						</p>
					</div>
					<!-- <div class="mb-4 h-1 w-10 rounded-full bg-tt-red"></div> -->
				</div>
			</div>
			<div class="grid grid-cols-8 gap-4 px-4 pb-4 pt-6">
				<Popover.Close class="col-span-2">
					<a
						data-sveltekit-noscroll
						href="/private-tours"
						data-sveltekit-prefetch
						class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
					>
						<enhanced:img
							loading="eager"
							alt="Private multi-day tours"
							class="absolute inset-0 h-full w-full object-cover"
							src="$lib/assets/images/private-tours-1.jpg"
						/>

						<div
							class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
						></div>

						<div class="relative mx-auto lg:mx-0">
							<h3 class="text-center text-lg font-bold text-background">Private Multi-day Tours</h3>
						</div>
						<div
							class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
						>
							<ArrowRight class="h-5 w-5 text-background" />
						</div>
					</a>
				</Popover.Close>
				<Popover.Close class="col-span-2">
					<a
						data-sveltekit-noscroll
						href="/custom-private-tours"
						data-sveltekit-prefetch
						class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
					>
						<enhanced:img
							loading="eager"
							alt="Private day tours"
							class="absolute inset-0 h-full w-full object-cover"
							src="$lib/assets/images/wineglass.jpg"
						/>

						<div
							class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
						></div>

						<div class="relative mx-auto lg:mx-0">
							<h3 class="text-center text-lg font-bold text-background">Private Day Tours</h3>
						</div>
						<div
							class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
						>
							<ArrowRight class="h-5 w-5 text-background" />
						</div>
					</a>
				</Popover.Close>
				<Popover.Close class="col-span-2">
					<a
						data-sveltekit-noscroll
						href="/curated-tasmania-experiences"
						data-sveltekit-prefetch
						class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
					>
						<enhanced:img
							loading="eager"
							alt="Bespoke Tasmania experiences"
							class="absolute inset-0 h-full w-full object-cover"
							src="$lib/assets/curated/curated-tours.jpg"
						/>

						<div
							class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
						></div>

						<div class="relative mx-auto lg:mx-0">
							<h3 class="text-center text-lg font-bold text-background">
								Bespoke Tasmania Experiences
							</h3>
						</div>
						<div
							class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
						>
							<ArrowRight class="h-5 w-5 text-background" />
						</div>
					</a>
				</Popover.Close>
				<Popover.Close class="col-span-2">
					<a
						data-sveltekit-noscroll
						href="/tasmanian-golf-experiences"
						data-sveltekit-prefetch
						class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
					>
						<enhanced:img
							loading="eager"
							alt="Private Tasmanian golf experiences"
							class="absolute inset-0 h-full w-full object-cover"
							src="$lib/assets/golf/links-dunes-sunset.jpg?w=800"
						/>

						<div
							class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
						></div>

						<div class="relative mx-auto lg:mx-0">
							<h3 class="text-center text-lg font-bold text-background">Golf Experiences</h3>
						</div>
						<div
							class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
						>
							<ArrowRight class="h-5 w-5 text-background" />
						</div>
					</a>
				</Popover.Close>
				<!-- {#each privateTours.slice(0, 6) as tour}
					<Popover.Close>
						<a
							data-sveltekit-noscroll
							href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
							data-sveltekit-prefetch
							class="group relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
						>
							{#if tour.images && tour.images.length > 0}
								<enhanced:img
									alt={tour.name}
									class="absolute inset-0 h-full w-full object-cover"
									src={Object.entries(enhancedImages).find(
										([key, value]) => key === tour.images[0].itemUrl
									)[1].default}	src={Object.entries(enhancedImages).find(
										([key]) => key === tour.images[0].itemUrl
									)?.[1]?.default || ''}
									sizes="(min-width: 1280px) 16vw, 50vw"
									loading="lazy"
								/>
							{:else}
								<File class="h-48 w-full object-cover text-muted-foreground" />
							{/if}
							<div
								class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
							></div>

							<div class="relative mx-auto lg:mx-0">
								<h3 class="text-center text-sm font-bold text-background">
									{tour.name.split('-')[1]}
								</h3>
							</div>
						</a>
					</Popover.Close>
				{/each} -->
			</div>
			<!-- <div class="flex rounded-xl px-6 pb-2">
				<Popover.Close>
					<Button
						href="/private-tours"
						variant="link"
						class="pl-0 text-sm font-semibold text-foreground"
					>
						Learn more <ArrowRight class="ml-2 h-4 w-4" />
					</Button>
				</Popover.Close>
				<Popover.Close>
					<Button
						href="/custom-private-tours"
						variant="link"
						class="pl-0 text-sm font-semibold text-foreground"
					>
						Custom private tours <ArrowRight class="ml-2 h-4 w-4" />
					</Button>
				</Popover.Close>
			</div> -->
		</Popover.Content>
	</Popover.Root>
	<Popover.Root>
		<Popover.Trigger class="flex items-center transition-colors hover:text-foreground/80">
			Shore excursions <ChevronDown class=" mt-1 h-4 w-4" />
		</Popover.Trigger>

		<Popover.Content
			class="flex w-[1250px] flex-col overflow-hidden rounded-xl p-0"
			align="start"
			sideOffset={24}
		>
			<div class="w-full bg-secondary">
				<div class="flex flex-col rounded-xl p-6">
					<h2
						class="scroll-m-20 pb-1 font-heading text-xl font-semibold tracking-tight transition-colors first:mt-0"
					>
						Shore Excursions
					</h2>
					<!-- <div class="mb-4 h-1 w-10 rounded-full bg-tt-red"></div> -->
					<p class="max-w-xl text-sm">
						Coming to Tasmania via Cruise ship and want to maximise your time while you are on dry
						land? We offer tours state wide to all the major tourist attractions at an affordable,
						fair price. We are a professional team who love showcasing Tasmania to people from all
						around the world.
					</p>
				</div>
			</div>
			<div class="grid grid-cols-6 gap-4 px-4 pt-6">
				{#each shoreExcursions.slice(0, 6) as tour}
					<Popover.Close>
						<a
							data-sveltekit-noscroll
							href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
							data-sveltekit-prefetch
							class="group relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
						>
							{#if tour.images && tour.images.length > 0}
								{#if isEnhancedImage(tour.images[0].itemUrl)}
									<enhanced:img
										alt={tour.name}
										class="absolute inset-0 h-full w-full object-cover"
										src={getImageSource(tour.images[0].itemUrl)}
										sizes="(min-width: 1280px) 16vw, 50vw"
										loading="lazy"
									/>
								{:else}
									<img
										alt={tour.name}
										class="absolute inset-0 h-full w-full object-cover"
										src={tour.images[0].itemUrl}
										sizes="(min-width: 1280px) 16vw, 50vw"
										loading="lazy"
									/>
								{/if}
							{:else}
								<File class="h-48 w-full object-cover text-muted-foreground" />
							{/if}
							<div
								class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
							></div>

							<div class="relative mx-auto lg:mx-0">
								<h3 class="text-md text-center font-bold text-background">{tour.name}</h3>
							</div>
							<div
								class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
							>
								<ArrowRight class="h-5 w-5 text-background" />
							</div>
						</a>
					</Popover.Close>
				{/each}
			</div>
			<div class="flex rounded-xl px-6 pb-2">
				<Popover.Close>
					<Button
						href="/tours#shoreExcursions"
						variant="link"
						class="pl-0 text-sm font-semibold text-foreground"
					>
						View more <ArrowRight class="ml-2 h-4 w-4" />
					</Button>
				</Popover.Close>
			</div></Popover.Content
		>
	</Popover.Root>
	<Popover.Root>
		<Popover.Trigger class="flex items-center transition-colors hover:text-foreground/80">
			Golf <ChevronDown class=" mt-1 h-4 w-4" />
		</Popover.Trigger>

		<Popover.Content
			class="flex w-[625px] flex-col overflow-hidden rounded-xl p-0"
			align="start"
			sideOffset={24}
		>
			<div class="w-full bg-secondary">
				<div class="flex flex-col rounded-xl p-6">
					<h2
						class="scroll-m-20 pb-1 font-heading text-xl font-semibold tracking-tight transition-colors first:mt-0"
					>
						Tasmanian Golf Experiences
					</h2>
					<p class="text-sm">
						Play Tasmania's world-class links and parkland courses with the local experts. Join our
						small-group Legends of Tasmania golf journey, or let us build a private golf trip around
						the courses, stays and experiences you want.
					</p>
				</div>
			</div>
			<div class="grid grid-cols-2 gap-4 px-4 pb-4 pt-6">
				<Popover.Close>
					<a
						data-sveltekit-noscroll
						href="/tours/legends-of-tasmania-golf-history-and-flavour-4-days-4-nights"
						data-sveltekit-prefetch
						class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
					>
						<enhanced:img
							loading="lazy"
							alt="Legends of Tasmania golf tour"
							class="absolute inset-0 h-full w-full object-cover"
							src="$lib/assets/golf/ratho-farm-golfer.jpg?w=800"
						/>
						<div
							class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
						></div>
						<div class="relative mx-auto lg:mx-0">
							<h3 class="text-center text-lg font-bold text-background">Legends of Tasmania</h3>
						</div>
						<div
							class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
						>
							<ArrowRight class="h-5 w-5 text-background" />
						</div>
					</a>
				</Popover.Close>
				<Popover.Close>
					<a
						data-sveltekit-noscroll
						href="/tasmanian-golf-experiences"
						data-sveltekit-prefetch
						class="group relative flex aspect-video h-[160px] w-full items-center justify-center overflow-hidden bg-gray-900 px-4 py-20 shadow-xl sm:rounded-xl"
					>
						<enhanced:img
							loading="lazy"
							alt="Tailored Tasmanian golf experiences"
							class="absolute inset-0 h-full w-full object-cover"
							src="$lib/assets/golf/coastal-links-aerial.jpg?w=800"
						/>
						<div
							class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all group-hover:bg-foreground/50"
						></div>
						<div class="relative mx-auto lg:mx-0">
							<h3 class="text-center text-lg font-bold text-background">Tailored Golf Experiences</h3>
						</div>
						<div
							class="absolute bottom-3 right-3 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-70"
						>
							<ArrowRight class="h-5 w-5 text-background" />
						</div>
					</a>
				</Popover.Close>
			</div>
		</Popover.Content>
	</Popover.Root>
	<Links />

	<a data-sveltekit-noscroll href="/blog" class="transition-colors hover:text-foreground/80">
		Blog
	</a>

	<a data-sveltekit-noscroll href="/faq" class="transition-colors hover:text-foreground/80">
		FAQ
	</a>
	<!-- <a data-sveltekit-noscroll href="/contact" class="transition-colors hover:text-foreground/80">
		Contact
	</a> -->
</nav>
